import { FixedHorizontalGeometryResolver } from './geometry-resolvers/fixed-horizontal-geometry-resolver.ts';
import {
  type AddHighlightOptions,
  DomHighlightsRenderer
} from './highlights-renderer.ts';
import {
  type HighlightsRenderingStrategy,
  type HighlightsRenderingStrategyContext
} from './highlights-rendering-strategy.ts';
import { ProjectedHighlightsRenderer } from './projected-highlights-renderer.ts';
import { type GeometryResolver } from './types.ts';

/**
 * Minimal table-layout port required to project highlights around the current
 * pinned-leading boundary. Implementations own how layout is read and observed;
 * the highlights package does not query Table V1 cells or React state.
 */
export interface TableHighlightsLayoutAdapter {
  /**
   * Returns the number of leading columns in the pinned coordinate space.
   */
  getPinnedLeadingColumnCount(): number;
  /**
   * Subscribes to changes that can move the pinned-leading boundary.
   */
  subscribeToLayoutChanges(listener: () => void): () => void;
}

interface TableHighlightsRenderingStrategyOptions extends HighlightsRenderingStrategyContext {
  readonly layout: TableHighlightsLayoutAdapter;
  readonly pinnedLeadingSurfaceZIndex: number;
  readonly getBaseFragmentZIndex: (options: AddHighlightOptions) => number;
  /**
   * Gates the null-bounds retry fix (#37398/#39072) behind the caller's
   * rollout experiment. Resolved once by the bootstrap composition root, not
   * read here. Defaults to enabled for callers that don't pass it.
   */
  readonly enableNullBoundsRetry?: boolean;
}

interface PinnedLayerLease {
  readonly renderer: DomHighlightsRenderer;
  release(): void;
}

/**
 * Creates the table-specific controller strategy.
 *
 * The strategy owns one layout subscription and one lazy pinned-leading surface
 * shared by every logical layer. Cell and column ranges are projected onto the
 * base surface, pinned-leading surface, or both; row, container, and hidden
 * ranges stay on the base surface. All fragments are created through direct DOM
 * renderers, with no React or ping-registry notification path.
 */
export function createTableHighlightsRenderingStrategy({
  container,
  resolver,
  invalidateGeometry,
  layout,
  pinnedLeadingSurfaceZIndex,
  getBaseFragmentZIndex,
  enableNullBoundsRetry = true
}: TableHighlightsRenderingStrategyOptions): HighlightsRenderingStrategy {
  const renderers = new Set<ProjectedHighlightsRenderer>();
  let unsubscribeLayout: (() => void) | null = null;
  let pinnedLeadingColumnCount = 0;
  let pinnedSurface: {
    readonly container: HTMLElement;
    readonly resolver: GeometryResolver;
    users: number;
  } | null = null;
  let disposed = false;

  function attachPinnedSurface() {
    const parent = resolver.getScrollContainer();

    if (parent && pinnedSurface && pinnedSurface.container.parentElement !== parent) {
      parent.insertBefore(pinnedSurface.container, parent.firstChild);
    }
  }

  function getOrCreatePinnedSurface() {
    if (!pinnedSurface) {
      const surfaceContainer = document.createElement('div');

      surfaceContainer.dataset.highlightSurface = 'pinned-leading';
      surfaceContainer.style.position = 'sticky';
      surfaceContainer.style.left = '0';
      surfaceContainer.style.width = '0';
      surfaceContainer.style.height = '0';
      surfaceContainer.style.flex = '0 0 0';
      surfaceContainer.style.pointerEvents = 'none';
      surfaceContainer.style.zIndex = pinnedLeadingSurfaceZIndex.toString();

      pinnedSurface = {
        container: surfaceContainer,
        resolver: new FixedHorizontalGeometryResolver(resolver),
        users: 0
      };
    }

    attachPinnedSurface();

    return pinnedSurface;
  }

  function acquirePinnedLayer(): PinnedLayerLease {
    const surface = getOrCreatePinnedSurface();
    const renderer = new DomHighlightsRenderer(
      surface.container,
      surface.resolver,
      undefined,
      enableNullBoundsRetry
    );
    let released = false;

    surface.users += 1;

    return {
      renderer,
      release() {
        if (released) {
          return;
        }

        released = true;
        renderer.dispose();
        surface.users -= 1;

        if (surface.users === 0 && pinnedSurface === surface) {
          surface.container.remove();
          pinnedSurface = null;
        }
      }
    };
  }

  function stopLayoutSubscriptionWhenUnused() {
    if (renderers.size > 0) {
      return;
    }

    unsubscribeLayout?.();
    unsubscribeLayout = null;
  }

  function ensureLayoutSubscription() {
    if (unsubscribeLayout) {
      return;
    }

    pinnedLeadingColumnCount = layout.getPinnedLeadingColumnCount();
    unsubscribeLayout = layout.subscribeToLayoutChanges(() => {
      pinnedLeadingColumnCount = layout.getPinnedLeadingColumnCount();
      invalidateGeometry();

      for (const renderer of renderers) {
        renderer.reprojectAll();
      }
    });
  }

  return {
    createLayer() {
      if (disposed) {
        throw new Error('Cannot create a highlight layer from a disposed strategy.');
      }

      ensureLayoutSubscription();

      const baseRenderer = new DomHighlightsRenderer(
        container,
        resolver,
        undefined,
        enableNullBoundsRetry
      );
      const renderer = new ProjectedHighlightsRenderer({
        baseRenderer,
        getPinnedColumnCount: () => pinnedLeadingColumnCount,
        getBaseFragmentZIndex,
        acquirePinnedLayer,
        onDispose: () => {
          renderers.delete(renderer);
          stopLayoutSubscriptionWhenUnused();
        }
      });

      renderers.add(renderer);

      return renderer;
    },

    dispose() {
      if (disposed) {
        return;
      }

      disposed = true;

      for (const renderer of [...renderers]) {
        renderer.dispose();
      }

      renderers.clear();
      unsubscribeLayout?.();
      unsubscribeLayout = null;
      pinnedSurface?.container.remove();
      pinnedSurface = null;
    }
  };
}
